import React, { useEffect, useState, useCallback } from 'react';
import { paymentMethodsApi, AutoTopupSettings as AutoTopupConfig } from '../services/api';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  advertiserId: string;
  /** Bumped by parent when methods change so we re-check has_default_method */
  refreshKey?: number;
}

const AutoTopupSettings: React.FC<Props> = ({ advertiserId, refreshKey }) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<AutoTopupConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local form state
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState<string>('10');
  const [topupAmount, setTopupAmount] = useState<string>('200');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await paymentMethodsApi.getAutoTopup(advertiserId);
      setConfig(c);
      setEnabled(c.enabled);
      setThreshold(c.threshold.toString());
      setTopupAmount(c.topup_amount.toString());
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('billing.errLoadAutoTopup'));
    } finally {
      setLoading(false);
    }
  }, [advertiserId, t]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await paymentMethodsApi.updateAutoTopup(advertiserId, {
        enabled,
        threshold: parseFloat(threshold) || 0,
        topup_amount: parseFloat(topupAmount) || 0,
      });
      setConfig(updated);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('billing.errSaveAutoTopup'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('billing.loadingDots')}</div>;
  if (!config) return null;

  const ccy = config.currency;
  const noMethod = !config.has_default_method;

  if (noMethod) {
    return (
      <div className="auto-topup-settings">
        <div className="auto-topup-header">
          <h3>{t('billing.autoTopup')}</h3>
          <p className="muted">{t('billing.autoTopupIntro')}</p>
        </div>
        <div className="auto-topup-empty">
          <div className="auto-topup-empty-icon">💳</div>
          <div className="auto-topup-empty-title">{t('billing.noSavedCard')}</div>
          <div className="auto-topup-empty-sub">
            <Trans i18nKey="billing.addCardInSavedCards" components={{ b: <strong /> }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auto-topup-settings">
      <div className="auto-topup-header">
        <h3>{t('billing.autoTopup')}</h3>
        <p className="muted">{t('billing.autoTopupIntro')}</p>
      </div>

      <div
        className={`auto-topup-toggle ${enabled ? 'on' : ''}`}
        onClick={() => setEnabled(!enabled)}
      >
        <div className="auto-topup-toggle-text">
          <div className="auto-topup-toggle-title">{t('billing.enableAutoTopup')}</div>
          <div className="auto-topup-toggle-sub">
            {t(enabled ? 'billing.autoOn' : 'billing.autoOff')}
          </div>
        </div>
        <div className="switch" role="switch" aria-checked={enabled}>
          <div className="switch-knob" />
        </div>
      </div>

      <div className="auto-topup-fields">
        <div className="auto-topup-field">
          <label>{t('billing.chargeBelow')}</label>
          <div className="input-with-suffix">
            <input
              type="number"
              min="0"
              step="0.01"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              disabled={!enabled}
            />
            <span className="suffix">{ccy}</span>
          </div>
        </div>

        <div className="auto-topup-field">
          <label>{t('billing.topUpBy')}</label>
          <div className="input-with-suffix">
            <input
              type="number"
              min="1"
              step="0.01"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
              disabled={!enabled}
            />
            <span className="suffix">{ccy}</span>
          </div>
        </div>
      </div>

      {error && <div className="alert-error">{error}</div>}

      <div className="form-actions">
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {t(saving ? 'billing.savingDots' : 'billing.saveSettings')}
        </button>
      </div>
    </div>
  );
};

export default AutoTopupSettings;
